import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'
import type { AppDispatch, RootState } from '@/store'
import { useAdicionarItensComandaMutation, useGetCatalogoQuery, useLazyGetContaTotemCheckoutQuery, type ProdutoCatalogo } from '@/api'
import { capitalize } from '@/lib/formato'
import { uuidLocal } from '@/lib/id'
import { itemDoCodigoBarras, resolverProdutoPorCodigo, type ResultadoCodigoBarras } from '@/lib/codigo-barras'
import { atualizarKiosk } from '@/store/self-service-kiosk-slice'
import { trocarView } from '../navegacao-kiosk'
import { useLeitorCodigoBarras } from './use-leitor-codigo-barras'

/** Uma leitura do leitor como aparece na lista do resumo; a que dá certo some quando o item chega na conta. */
export type LeituraNaTela =
  | { id: number; estado: 'ADICIONANDO'; nome: string }
  | { id: number; estado: 'RECUSADA'; texto: string }

export interface LeitorResumo {
  leituras: LeituraNaTela[]
  /** Um produto entrando (e os 2 s depois): leitura nesse meio é descartada. */
  pausado: boolean
}

const TEMPO_RECUSA_NA_TELA_MS = 5000
// O mesmo produto "bipado" duas vezes seguidas lançava dois: depois de cada um, o leitor espera antes do próximo.
const PAUSA_DEPOIS_DE_ADICIONAR_MS = 2000

function motivoDaRecusa(resultado: Exclude<ResultadoCodigoBarras, { status: 'ENCONTRADO' }>): string {
  switch (resultado.status) {
    case 'NAO_ENCONTRADO':
      return 'Produto não encontrado. Confira o código ou chame um atendente.'
    case 'DUPLICADO':
      return 'Este código está em mais de um produto. Chame um atendente.'
    case 'COM_OPCOES':
      return `${capitalize(resultado.produto.descricao)} tem opções: use Adicionar item.`
    case 'PESAVEL':
      return `${capitalize(resultado.produto.descricao)} é vendido por peso: use a balança ou chame um atendente.`
    case 'ETIQUETA_BALANCA':
      return 'Etiqueta de balança não é aceita aqui. Chame um atendente.'
  }
}

/**
 * Leitor de código de barras no resumo do totem de checkout: o produto lido entra direto na comanda, pelo
 * mesmo caminho do "Adicionar item". Um produto por vez: enquanto ele entra, e por 2 s depois, o leitor pausa.
 */
export function useLeitorResumo(ativo: boolean): LeitorResumo {
  const dispatch = useDispatch<AppDispatch>()
  const store = useStore<RootState>()
  const { data: catalogo, isError: catalogoFalhou } = useGetCatalogoQuery('CARDAPIO_TOTEM_CHECKOUT')
  const [adicionarItens] = useAdicionarItensComandaMutation()
  const [buscarConta] = useLazyGetContaTotemCheckoutQuery()
  const [leituras, setLeituras] = useState<LeituraNaTela[]>([])
  // O ref decide na hora da leitura; o estado só desenha a dica de "aguarde".
  const pausado = useRef(false)
  const [pausadoNaTela, setPausadoNaTela] = useState(false)
  const proximoId = useRef(0)
  const catalogoAtual = useRef({ catalogo, catalogoFalhou })
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([])

  useEffect(() => {
    catalogoAtual.current = { catalogo, catalogoFalhou }
  })

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), [])

  const pausar = useCallback((valor: boolean): void => {
    pausado.current = valor
    setPausadoNaTela(valor)
  }, [])

  const recusar = useCallback((id: number, texto: string): void => {
    setLeituras((atuais) => [...atuais.filter((l) => l.id !== id), { id, estado: 'RECUSADA', texto }])
    timers.current.push(setTimeout(() => setLeituras((atuais) => atuais.filter((l) => l.id !== id)), TEMPO_RECUSA_NA_TELA_MS))
  }, [])

  const enviar = useCallback(async (produto: ProdutoCatalogo): Promise<void> => {
    proximoId.current += 1
    const id = proximoId.current
    // Lido na hora do envio: a leitura anterior pode ter acabado de criar o pedido na comanda.
    const { identificadorBuscado: identificador, order } = store.getState().selfServiceKiosk
    pausar(true)
    setLeituras((atuais) => [...atuais, { id, estado: 'ADICIONANDO', nome: capitalize(produto.descricao) }])

    try {
      await adicionarItens({
        identificador,
        itens: [itemDoCodigoBarras(produto)],
        idPedidoIntegracao: order?.idPedidoIntegracao ?? undefined,
        chaveIdempotencia: uuidLocal(),
      }).unwrap()

      const conta = await buscarConta({ identificador }).unwrap()
      const pedidos = conta.pedidos ?? []
      const mesmoPedido = order?.idPedidoIntegracao ? pedidos.find((p) => p.idPedidoIntegracao === order.idPedidoIntegracao) : undefined

      // Continua no pedido que o cliente está vendo; só volta à escolha se não souber qual era.
      if (mesmoPedido) {
        dispatch(atualizarKiosk({ order: mesmoPedido, multipleOrders: pedidos, hasMultipleOrders: pedidos.length > 1 }))
      } else if (pedidos.length > 1) {
        trocarView(dispatch, { currentView: 'VIEW_MULTIPLE_ORDERS', multipleOrders: pedidos, hasMultipleOrders: true })
      } else {
        dispatch(atualizarKiosk({ order: pedidos[0] ?? null, hasMultipleOrders: false }))
      }

      setLeituras((atuais) => atuais.filter((l) => l.id !== id))
      timers.current.push(setTimeout(() => pausar(false), PAUSA_DEPOIS_DE_ADICIONAR_MS))
    } catch (err) {
      console.error('Erro ao adicionar o produto lido à comanda:', err)
      const mensagem = (err as { data?: { mensagem?: string } })?.data?.mensagem
      recusar(id, mensagem ?? 'Não foi possível adicionar o produto. Tente novamente.')
      // Não entrou nada: o cliente pode passar de novo na hora.
      pausar(false)
    }
  }, [store, adicionarItens, buscarConta, dispatch, pausar, recusar])

  const aoLer = useCallback((codigo: string): void => {
    const { catalogo: cat, catalogoFalhou: falhou } = catalogoAtual.current
    const recusarNova = (texto: string): void => {
      proximoId.current += 1
      recusar(proximoId.current, texto)
    }

    if (!cat) {
      recusarNova(falhou ? 'Não foi possível consultar os produtos. Chame um atendente.' : 'Ainda carregando os produtos. Passe o produto de novo em instantes.')
      return
    }

    const resultado = resolverProdutoPorCodigo(cat, codigo)
    if (resultado.status === 'ENCONTRADO') void enviar(resultado.produto)
    else recusarNova(motivoDaRecusa(resultado))
  }, [enviar, recusar])

  // Pausado, a leitura é descartada — mas o Enter dela continua preso, para não apertar o botão focado.
  const ignorar = useCallback((codigo: string): boolean => {
    if (pausado.current) return true

    const { identificadorBuscado, order } = store.getState().selfServiceKiosk
    return codigo === identificadorBuscado || codigo === order?.identificadorSecundario
  }, [store])

  useLeitorCodigoBarras({ ativo, aoLer, ignorar })

  return { leituras, pausado: pausadoNaTela }
}
